<template>
  <SubHeader v-if="dossierShowItems" :title="`Dossier: ${dossierShowItems.complaintsubcategory?.label ?? '-'}`"
    :breads="[{ name: 'Détails dossier' }]" :action="{
      to: 'DossierListFilter',
      params: { filter: checkGuard('view_closed_only') ? 'no_follow_up' : 'all' },
      title: 'Revernir sur la liste des plaintes',
    }" />
  <div v-if="dossierShowIsLoading" class="loading-overlay">
    <span class="loading-spinner">Chargement...</span>
    <div class="spinner"></div>
  </div>
  <div v-else class="row">
    <div class="col-md-12">
      <!-- Card -->
      <div class="card">
        <!-- Informations principales -->
        <div class="d-flex justify-content-between align-items-center p-2 border-bottom">
          <div class="d-flex align-items-center flex-wrap gap-2 col-md-5">
            <span class="badge bg-soft-primary text-black" style="font-size: 10px">
              Référence : {{ dossierShowItems.reference }}
            </span>
            <span class="badge bg-soft-secondary text-black" style="font-size: 10px">
              Créé le : {{ dossierShowItems.created_at }}
            </span>
            <span class="badge bg-soft-dark text-black" style="font-size: 10px">
              Institution :
              {{ dossierShowItems.institution?.label || "Non spécifié" }}
            </span>
            <span class="badge bg-soft-dark text-black" style="font-size: 10px">
              Division :
              {{ dossierShowItems.division?.label || "Non spécifiée" }}
            </span>
            <span v-if="dossierShowItems.complaint_status" class="badge"
              :class="getStatusBadgeClass(dossierShowItems.complaint_status.label)" style="font-size: 10px">
              Statut : {{ dossierShowItems.complaint_status.description }}
            </span>
            <span v-if="dossierShowItems.lastname || dossierShowItems.firstname" class="badge bg-soft-info text-black" style="font-size: 10px">
              Plaignant : {{ dossierShowItems.lastname || '' }} {{ dossierShowItems.firstname || '' }}
            </span>
            <span v-if="dossierShowItems.phone" class="badge bg-soft-success text-black" style="font-size: 10px">
              <i class="bi bi-telephone me-1"></i>{{ dossierShowItems.phone }}
            </span>
            <span v-if="dossierShowItems.email" class="badge bg-soft-warning text-dark" style="font-size: 10px">
              <i class="bi bi-envelope me-1"></i>{{ dossierShowItems.email }}
            </span>
            <span v-if="dossierShowItems.tracknumber && dossierShowItems.tracknumber !== '0'" class="badge bg-soft-primary text-black" style="font-size: 10px">
              <i class="bi bi-hash me-1"></i>{{ dossierShowItems.tracknumber }}
            </span>
          </div>

          <div class="col-md-7 d-flex justify-content-end gap-2">
            <!-- Indication d'affectation existante -->
            <div v-if="isAffected && !checkGuard('view_closed_only')" class="alert alert-warning alert-sm mb-0 py-1 px-2 d-flex align-items-center"
              style="font-size: 12px;">
              <i class="bi bi-info-circle me-1"></i>
              Cette plainte a déjà été affectée
              <span v-if="dossierShowItems.affectation_details">
                à {{ getAffectationDetails() }}
              </span>
            </div>

            <!-- Boutons de navigation -->
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="goToPrevious"
              :disabled="!hasPrevious" title="Plainte précédente">
              <i class="bi bi-chevron-left"></i>
            </button>
            <span class="align-self-center" style="font-size: 12px;">
              <span v-if="dossiersList.length > 0">
                {{ currentIndex + 1 }} / {{ dossiersList.length }}
              </span>
              <span v-else class="text-muted">
                Chargement...
              </span>
            </span>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="goToNext" :disabled="!hasNext"
              title="Plainte suivante">
              <i class="bi bi-chevron-right"></i>
            </button>

            <!-- Bouton d'affectation -->
            <button v-if="!checkGuard('view_closed_only')" type="button" class="btn btn-primary btn-sm" data-bs-toggle="modal"
              data-bs-target="#affectationModal" id="affectationModalButton">
              <i class="bi bi-arrow-up-right-square me-1"></i>
              {{ isAffected ? 'Réaffecter' : 'Affecter' }}
            </button>
          </div>
        </div>

        <!-- Tags et caractérisation -->
        <div class="d-flex flex-wrap align-items-center gap-3 p-3 border-bottom">
          <!-- Tags -->
          <div class="d-flex flex-wrap gap-2 flex-grow-1">
            <span v-for="(value, key) in parsedInsight(
              dossierShowItems.ai_data?.insights
            )" :key="key" :class="getBadgeClass(key)">
              {{ key }}: {{ value }}
            </span>

            <span v-for="(caracter, i) in dossierShowItems.caracterisation" :key="i" :class="getBadgeClass(i)">
              {{ caracter.name }}:
              <span v-for="(item, index) in caracter.elements" :key="index">{{ item.name }}
                {{ index !== caracter.elements.length - 1 ? ", " : "" }}
              </span>
            </span>
          </div>

          <!-- Lecteur audio et téléchargement -->
          <div class="d-flex align-items-center gap-3 flex-shrink-0">
            <audio v-if="dossierShowItems.tracknumber != 0" controls style="max-width: 400px">
              <source :src="getAudioUrl(dossierShowItems.tracknumber)" type="audio/mp3" />
              Votre navigateur ne supporte pas la lecture audio.
            </audio>
            <button v-if="dossierShowItems.tracknumber != 0" @click="downloadAudio"
              class="btn btn-outline-success btn-sm" title="Télécharger l'audio">
              <i class="bi bi-download"></i>
            </button>
          </div>
        </div>

        <!-- Résumé et Transcription -->
        <div class="card-body">
          <!-- Description (toujours affichée) -->
          <div class="mb-4">
            <h5>Description</h5>
            <p>
              {{
                dossierShowItems.description ||
                "Aucune description disponible"
              }}
            </p>
          </div>

          <div v-if="dossierShowItems.llmstatus == 'COMPLETE'">
            <div class="mb-4">
              <h5>Mots Clés</h5>
              <div class="d-flex flex-wrap gap-2">
                <span v-for="(value, key) in parsedKeywords(
                  dossierShowItems.ai_data?.keywords
                )" :key="key" class="badge bg-info">
                  {{ value }}
                </span>
              </div>
            </div>

            <div class="mb-4">
              <h5>
                Résumé IA – Powered by Foniva<i class="bi bi-robot mx-2" style="font-size: 20px"></i>
              </h5>
              <p>
                {{
                  parsedSummary(dossierShowItems.ai_data?.summary) ||
                  "Résumé indisponible"
                }}
              </p>
            </div>

            <div class="mb-4">
              <h5>
                Transcription IA complète - Powered by Foniva<i class="bi bi-robot mx-2" style="font-size: 20px"></i>
              </h5>
              <div class="" v-if="dossierShowItems.ai_data?.transcription">
                <pre style="font-family: 'Montserrat'; color: black" v-if="
                  !dossierShowItems.ai_data?.transcription?.includes('summary')
                " class="mt-0 text-sm rounded">
                  {{ dossierShowItems.ai_data?.transcription }}
                </pre>
                <p v-else>
                  {{ parsedTranscription(dossierShowItems.ai_data?.transcription) }}
                </p>
              </div>
              <span v-else> Transcription non fournie</span>
            </div>
          </div>
          <div v-else-if="dossierShowItems.tracknumber != 0">
            <p class="text-center">La plainte est en cours de transcription</p>
          </div>
        </div>

        <!-- Section Pièces Jointes -->
        <div class="card-body border-top">
          <FilePreview v-if="dossierShowItems && dossierShowItems.id" :complaint-id="dossierShowItems.id" />
        </div>
      </div>
    </div>

    <!-- Button trigger modal -->
    <button v-if="!checkGuard('view_closed_only')" type="button" class="btn btn-primary" data-bs-toggle="modal" data-bs-target="#affectationModal"
      id="affectationModalButtonClose" ref="affectationModalClose" style="display: none"></button>
    <!-- End Button trigger modal -->

    <!-- Modal d'affectation -->
    <div v-if="!checkGuard('view_closed_only')" id="affectationModal" class="modal fade bd-example-modal-xl" tabindex="-1" role="dialog"
      aria-labelledby="affectationModalTitle" aria-hidden="true">
      <form id="affectationModalForm" @submit.prevent="prepareBeforeAffecte">

        <div class="modal-dialog modal-dialog-centered modal-xl" role="document">

          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title" id="affectationModalTitle">AFFECTATION</h5>
              <button type="button" class="btn-close" v-if="!affectationPlainteIsLoading" data-bs-dismiss="modal"
                aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <div class="mb-3 tom-select-custom">
                <label class="form-label" for="affectation_mode">Type d'affectation</label>
                <m-select v-model="affectationData.affectation_mode_select" :loading="affectationIsLoading"
                  :required="true" :options="affectationItems" :allow-empty="false" :searchable="false"
                  placeholder="Type d'affectation" track-by="id" label="label">
                </m-select>
              </div>

              <div id="affectation_mode_1" v-if="affectationData.affectation_mode == 1">
                <div class="row">
                  <div class="col-md-12">
                    <div class="mb-3">
                      <label class="form-label" for="affectation_analystes">Analyste(s)</label>
                      <m-select v-model="affectationData.affectation_analystes_select" :loading="userIsLoading"
                        :required="true" :options="userItems" :multiple="true" :allow-empty="false" :searchable="true"
                        placeholder="Rechercher des analystes..." track-by="id" label="firstname">
                      </m-select>
                      <small class="text-muted">
                        {{ affectationData.affectation_analystes_select?.length || 0 }} analyste(s) sélectionné(s)
                      </small>
                    </div>
                  </div>

                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label" for="affectation_division">Division</label>
                      <m-select v-model="affectationData.affectation_division" :loading="divisionIsLoading"
                        :required="!createNewDivision" :options="divisionItems" :allow-empty="true" :searchable="true"
                        placeholder="Rechercher une division..." track-by="id" label="label">
                      </m-select>
                      <div class="mt-2">
                        <button type="button" class="btn btn-sm btn-outline-primary" @click="toggleCreateDivision">
                          <i class="bi bi-plus-circle me-1"></i>
                          Créer une division
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-6">
                    <div class="mb-3">
                      <label class="form-label" for="affectation_secteur">Secteur</label>
                      <m-select v-model="affectationData.affectation_secteur" :loading="secteurIsLoading"
                        :required="!createNewSecteur" :options="secteurItems" :allow-empty="true" :searchable="true"
                        placeholder="Rechercher un secteur..." track-by="id" label="label">
                      </m-select>
                      <div class="mt-2">
                        <button type="button" class="btn btn-sm btn-outline-primary" @click="toggleCreateSecteur">
                          <i class="bi bi-plus-circle me-1"></i>
                          Créer un secteur
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Formulaire création Division -->
                  <div v-if="createNewDivision" class="col-md-12 mb-3">
                    <div class="card border-info">
                      <div class="card-header bg-info text-white py-2">
                        <h6 class="mb-0">🏢 Nouvelle Division</h6>
                      </div>
                      <div class="card-body">
                        <div class="row">
                          <div class="col-md-8">
                            <div class="mb-2">
                              <label class="form-label">Nom de la division *</label>
                              <input type="text" class="form-control form-control-sm" v-model="newDivisionData.label"
                                placeholder="Ex: Division des affaires juridiques">
                            </div>
                          </div>
                          <div class="col-md-4">
                            <div class="mb-2">
                              <label class="form-label">Code</label>
                              <input type="text" class="form-control form-control-sm" v-model="newDivisionData.code"
                                placeholder="Ex: DIV-001">
                            </div>
                          </div>
                        </div>
                        <button type="button" class="btn btn-success btn-sm" @click="createNewDivisionOnly"
                          :disabled="!newDivisionData.label || creatingDivision">
                          <span v-if="creatingDivision">
                            <span class="spinner-border spinner-border-sm me-1"></span>
                            Création...
                          </span>
                          <span v-else>
                            <i class="bi bi-plus-circle me-1"></i>
                            Créer
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Formulaire création Secteur -->
                  <div v-if="createNewSecteur" class="col-md-12 mb-3">
                    <div class="card border-warning">
                      <div class="card-header bg-warning text-dark py-2">
                        <h6 class="mb-0">🏢 Nouveau Secteur</h6>
                      </div>
                      <div class="card-body">
                        <div class="row">
                          <div class="col-md-6">
                            <div class="mb-2">
                              <label class="form-label">Nom du secteur *</label>
                              <input type="text" class="form-control form-control-sm" v-model="newSecteurData.label"
                                placeholder="Ex: Secteur des investigations">
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="mb-2">
                              <label class="form-label">Division associée *</label>
                              <select class="form-control form-control-sm" v-model="newSecteurData.division_id"
                                required>
                                <option :value="null">Sélectionner une division...</option>
                                <option :value="division.id" v-for="(division, index) in divisionItems" :key="index">
                                  {{ division.label }}
                                </option>
                              </select>
                            </div>
                          </div>
                          <div class="col-md-6">
                            <div class="mb-2">
                              <label class="form-label">Code</label>
                              <input type="text" class="form-control form-control-sm" v-model="newSecteurData.code"
                                placeholder="Ex: SEC-001">
                            </div>
                          </div>
                        </div>
                        <button type="button" class="btn btn-success btn-sm" @click="createNewSecteurOnly"
                          :disabled="!newSecteurData.label || !newSecteurData.division_id || creatingSecteur">
                          <span v-if="creatingSecteur">
                            <span class="spinner-border spinner-border-sm me-1"></span>
                            Création...
                          </span>
                          <span v-else>
                            <i class="bi bi-plus-circle me-1"></i>
                            Créer
                          </span>
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="col-md-12">
                    <div class="mb-3">
                      <label class="form-label" for="affectation_observations">Dispositions
                        particulières</label>
                      <textarea id="affectation_observations" v-model="affectationData.affectation_observations1"
                        name="affectation_observations" class="form-control"
                        placeholder="Dispositions particulières à observer lors du traitement du dossier. Appel à recourir à une documentation particulière"
                        rows="4"></textarea>
                    </div>
                  </div>
                </div>
              </div>
              <div id="affectation_mode_2" v-if="affectationData.affectation_mode == 2">
                <div class="mb-3 tom-select-custom">
                  <label class="form-label" for="affectation_institution">Administration</label>
                  <m-select v-model="affectationData.affectation_institution_select" :loading="institutionIsLoading"
                    :required="!createNewInstitution" :options="institutionItems" :allow-empty="true" :searchable="true"
                    :taggable="false" placeholder="Rechercher une administration..." track-by="id" label="label">
                  </m-select>
                  <small class="text-muted">
                    <i class="bi bi-info-circle me-1"></i>
                    Si vous ne trouvez pas l'institution, cochez la case ci-dessous pour en créer une nouvelle.
                  </small>
                </div>

                <div class="mb-3">
                  <div class="form-check">
                    <input class="form-check-input" type="checkbox" id="createNewInstitution"
                      v-model="createNewInstitution" @change="resetInstitutionSelection">
                    <label class="form-check-label fw-bold text-primary" for="createNewInstitution">
                      <i class="bi bi-plus-circle me-1"></i>
                      Créer une nouvelle institution
                    </label>
                  </div>
                </div>

                <div v-if="createNewInstitution" class="card border-primary mb-3">
                  <div class="card-header bg-primary text-white">
                    <h6 class="mb-0">🏢 Nouvelle Institution</h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label" for="new_institution_label">Nom de l'institution *</label>
                      <input type="text" class="form-control" id="new_institution_label"
                        v-model="newInstitutionData.label" placeholder="Ex: Ministère de la Santé"
                        :required="createNewInstitution">
                    </div>
                    <div class="row">
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label" for="new_institution_email">Email</label>
                          <input type="email" class="form-control" id="new_institution_email"
                            v-model="newInstitutionData.email" placeholder="contact@institution.bj">
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-3">
                          <label class="form-label" for="new_institution_phone">Téléphone</label>
                          <input type="text" class="form-control" id="new_institution_phone"
                            v-model="newInstitutionData.phone" placeholder="+229 XX XX XX XX">
                        </div>
                      </div>
                    </div>
                    <div class="mb-3">
                      <label class="form-label text-muted" for="new_institution_type">Type d'institution
                        (optionnel)</label>
                      <select class="form-select" id="new_institution_type" v-model="newInstitutionData.type">
                        <option :value="null">Non spécifié</option>
                        <option v-for="type in institutiontypeItems" :key="type.id" :value="type.id">
                          {{ type.label }}
                        </option>
                      </select>
                    </div>
                    <div class="d-grid gap-2">
                      <button type="button" class="btn btn-success" @click="createNewInstitutionOnly"
                        :disabled="!newInstitutionData.label || creatingInstitution">
                        <span v-if="creatingInstitution">
                          <span class="spinner-border spinner-border-sm me-2"></span>
                          Création en cours...
                        </span>
                        <span v-else>
                          <i class="bi bi-plus-circle me-2"></i>
                          Créer l'institution
                        </span>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div id="affectation_mode_3" v-if="affectationData.affectation_mode == 3">
                <div class="mb-3 tom-select-custom">
                  <label class="form-label" for="affectation_motif">Motif</label>
                  <m-select v-model="affectationData.affectation_motif_select" :loading="motifIsLoading"
                    :required="true" :options="motifItems" :allow-empty="false" :searchable="false"
                    placeholder="Choisissez un motif" track-by="id" label="label">
                  </m-select>
                </div>
                <div class="mb-3">
                  <label class="form-label" for="affectation_observations1">Autres details</label>
                  <textarea id="affectation_observations1" name="affectation_observations1" class="form-control"
                    placeholder="" v-model="affectationData.affectation_observations1" rows="4"></textarea>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-white" v-if="!affectationPlainteIsLoading"
                data-bs-dismiss="modal">Fermer</button>
              <button type="submit" class="btn btn-primary" v-if="!affectationPlainteIsLoading"
                id="affectation_submit_button">Enregistrer</button>
              <button type="button" class="btn btn-primary" disabled v-else id="affectation_submit_button">En
                cours de chargement</button>
            </div>
          </div>

        </div>
      </form>
    </div>
    <!-- End Modal -->


  </div>
</template>

<script>
import SubHeader from "@/layouts/admin/SubHeader.vue";
import FilePreview from "@/components/FilePreview.vue";
import { SERVER_FILE } from "@/utils/backend";
import backend from "@/utils/backend";
import { mapActions } from "vuex";
import { mapFields } from "vuex-map-fields";
import notify from "@/utils/notify.js";
import { checkGuard } from "@/utils/guards.js";

export default {
  name: "DossierShow",
  components: { SubHeader, FilePreview },
  data() {
    return {
      colHistory: [
        {
          field: "id",
          title: "",
          sort: false,
          width: "0px",
          type: "number",
          cellRenderer() {
            return "";
          },
        },
        { field: "name", title: "Source" },
        { field: "permissions", title: "Fichier" },
        {
          field: "action",
          title: "Actions",
          sort: false,
          width: "10px",
          headerClass: "justify-center",
          cellClass: "justify-center",
        },
      ],
      colPiece: [
        {
          field: "id",
          title: "",
          sort: false,
          width: "0px",
          type: "number",
          cellRenderer() {
            return "";
          },
        },
        { field: "name", title: "Source" },
        { field: "display", title: "Fichier" },
        {
          field: "action",
          title: "Actions",
          sort: false,
          width: "10px",
          headerClass: "justify-center",
          cellClass: "justify-center",
        },
      ],
      affectationData: {},
      createNewInstitution: false,
      creatingInstitution: false,
      newInstitutionData: {
        label: '',
        email: '',
        phone: '',
        type: null
      },
      institutiontypeItems: [],
      institutiontypeIsLoading: false,
      createNewDivision: false,
      creatingDivision: false,
      newDivisionData: {
        label: '',
        code: ''
      },
      createNewSecteur: false,
      creatingSecteur: false,
      newSecteurData: {
        label: '',
        code: '',
        division_id: null
      },
      dossiersList: [],
      currentIndex: 0,
    };
  },

  computed: {
    ...mapFields("dossier/show", {
      dossierShowItems: "item",
      dossierShowIsLoading: "isLoading",
      dossierShowIsSuccess: "isSuccess",
    }),
    ...mapFields("dossier/affectation", {
      affectationPlainteIsLoading: "isLoading",
      affectationPlainteIsSuccess: "isSuccess",
      affectationPlainteShowMessage: "showMessage",
      affectationPlainteMessage: "message",
      affectationPlainteIsCreated: "isUpdated",
      affectationPlainteItems: "updated"
    }),
    ...mapFields("affectation/list", {
      affectationIsLoading: "isLoading",
      affectationIsSuccess: "isSuccess",
      affectationShowMessage: "showMessage",
      affectationMessage: "message",
      affectationItems: "items"
    }),
    ...mapFields("user/list", {
      userIsLoading: "isLoading",
      userIsSuccess: "isSuccess",
      userShowMessage: "showMessage",
      userMessage: "message",
      userItems: "items"
    }),
    ...mapFields("institution/list", {
      institutionIsLoading: "isLoading",
      institutionIsSuccess: "isSuccess",
      institutionShowMessage: "showMessage",
      institutionMessage: "message",
      institutionItems: "items"
    }),
    ...mapFields("division/list", {
      divisionIsLoading: "isLoading",
      divisionIsSuccess: "isSuccess",
      divisionShowMessage: "showMessage",
      divisionMessage: "message",
      divisionItems: "items"
    }),
    ...mapFields("secteur/list", {
      secteurIsLoading: "isLoading",
      secteurIsSuccess: "isSuccess",
      secteurShowMessage: "showMessage",
      secteurMessage: "message",
      secteurItems: "items"
    }),
    ...mapFields("motif/list", {
      motifIsLoading: "isLoading",
      motifIsSuccess: "isSuccess",
      motifShowMessage: "showMessage",
      motifMessage: "message",
      motifItems: "items"
    }),
    ...mapFields("dossier/filter", {
      filterItems: "items",
      filterIsLoading: "isLoading",
      filterMeta: "currentItem"
    }),
    hasPrevious() {
      return this.currentIndex > 0 && this.dossiersList.length > 0;
    },
    hasNext() {
      return this.currentIndex < this.dossiersList.length - 1 && this.dossiersList.length > 0;
    },
    isAffected() {
      if (!this.dossierShowItems) return false;

      // Vérifier si treatment_id est non null (plainte affectée/traitée)
      const hasTreatment = this.dossierShowItems.treatment_id !== null &&
        this.dossierShowItems.treatment_id !== undefined;

      // Vérifier s'il y a une division OU un secteur assigné (affectation interne)
      const hasInternalAffectation = this.dossierShowItems.division || this.dossierShowItems.secteur;

      // Vérifier s'il y a des analystes assignés
      const hasAnalystes = this.dossierShowItems.analystes && this.dossierShowItems.analystes.length > 0;

      return hasTreatment || hasInternalAffectation || hasAnalystes;
    },
  },

  methods: {
    ...mapActions({
      getDossier: "dossier/show/getOne",
      affectationHandleSubmit: "dossier/affectation/update",
      resetAffectationCreate: "dossier/affectation/resetData",
      getAffectations: "affectation/list/getAll",
      resetAffectations: "affectation/list/resetData",
      getSecteurs: "secteur/list/getAll",
      resetSecteurs: "secteur/list/resetData",
      getDivisions: "division/list/getAll",
      resetDivisions: "division/list/resetData",
      getMotifs: "motif/list/getAll",
      resetMotifs: "motif/list/resetData",
      getInstitutions: "institution/list/getAll",
      resetInstitutions: "institution/list/resetData",
      getUsers: "user/list/getAll",
      resetUsers: "user/list/resetData",
    }),
    checkGuard,

    downloadAudio() {
      if (this.dossierShowItems.tracknumber) {
        const audioUrl = this.getAudioUrl(this.dossierShowItems.tracknumber);
        const link = document.createElement('a');
        link.href = audioUrl;
        link.download = `audio_${this.dossierShowItems.reference}.mp3`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        notify('Téléchargement de l\'audio démarré', 'success');
      }
    },

    limitText(text, limit) {
      if (!text) return "";
      return text.length > limit ? text.substring(0, limit) + "..." : text;
    },
    getAudioUrl(audioFile) {
      if (audioFile) {
        return `${process.env.VUE_APP_AUDIO_AND_IA_URL}/audio/${audioFile}`;
      }
    },

    getAudioUrlold(audioFile) {
      if (!audioFile) return "";
      const validExtensions = /\.(mp3|wav|ogg|flac|m4a)$/i;
      const hasValidExtension = validExtensions.test(audioFile);
      const fileName = hasValidExtension ? audioFile : `${audioFile}.mp3`;
      return `${SERVER_FILE}/${fileName}`;
    },

    parsedInsight(insights) {
      if (!insights) {
        return {};
      }
      // Si insights est déjà un objet, on le retourne direct
      if (typeof insights === 'object') {
        return insights;
      }
      // Si c'est une string simple (pas du JSON), on retourne un objet vide
      if (typeof insights === 'string' && !insights.trim().startsWith('{')) {
        return {};
      }
      try {
        const parsed = JSON.parse(insights);
        if (parsed.Additionnel) {
          const additionnel = parsed.Additionnel;
          delete parsed.Additionnel;
          for (const [key, value] of Object.entries(additionnel)) {
            const formattedKey = key.replace(/_/g, " ");
            parsed[formattedKey] = value;
          }
        }
        return parsed;
      } catch (error) {
        // JSON malformé (tronqué, etc.), on retourne un objet vide
        return {};
      }
    },

    parsedCaracterisation(caracterisation) {
      if (!caracterisation) {
        return [];
      }
      // Si c'est déjà un tableau, on le retourne
      if (typeof caracterisation === 'object') {
        return caracterisation;
      }
      // Si c'est une string simple (pas du JSON), on retourne un tableau vide
      if (typeof caracterisation === 'string' && !caracterisation.trim().startsWith('[')) {
        return [];
      }
      try {
        return JSON.parse(caracterisation);
      } catch (error) {
        return [];
      }
    },

    parsedKeywords(keywords) {
      if (!keywords) {
        return [];
      }
      // Si c'est déjà un tableau, on le retourne
      if (typeof keywords === 'object') {
        return keywords;
      }
      // Si c'est une string simple (pas du JSON), on retourne un tableau vide
      if (typeof keywords === 'string' && !keywords.trim().startsWith('{')) {
        return [];
      }
      try {
        return JSON.parse(keywords).keywords;
      } catch (error) {
        // JSON malformé, on retourne un tableau vide
        return [];
      }
    },

    parsedSummary(summary) {
      if (!summary) {
        return;
      }
      // Si summary est déjà une string simple (pas du JSON), on la retourne direct
      if (typeof summary === 'string' && !summary.trim().startsWith('{')) {
        return summary;
      }
      // Sinon on essaie de parser comme JSON
      try {
        const parsed = JSON.parse(summary);
        return parsed.summary || summary;
      } catch (error) {
        return summary;
      }
    },
    parsedTranscription(transcription) {
      try {
        if (!transcription) {
          return '';
        }
        return JSON.parse(transcription)?.summary;
      } catch (e) {
        return transcription;
      }
    },

    parseHistory(historyString) {
      if (typeof historyString == "object") {
        return historyString;
      }
      try {
        return JSON.parse(historyString || "[]");
      } catch (e) {
        return [];
      }
    },

    parseFile(file) {
      try {
        return JSON.parse(file || "[]");
      } catch (e) {
        return [];
      }
    },

    getBadgeClass(key) {
      switch (key) {
        case "Sentiment":
          return "badge badge-ia bg-primary";
        case "Urgence":
          return "badge badge-ia bg-danger";
        case "Impact":
          return "badge badge-ia bg-warning text-dark";
        case "Cout":
          return "badge badge-ia bg-secondary";
        case "Légal":
          return "badge badge-ia bg-info";
        case "Niveau de menace":
          return "badge badge-ia bg-danger";
        case "Crédibilité":
          return "badge badge-ia bg-success";
        default:
          return "badge badge-ia bg-warning text-dark";
      }
    },

    getStatusBadgeClass(statusLabel) {
      if (!statusLabel) return 'bg-secondary';

      const status = statusLabel.toLowerCase();
      if (status.includes('affect') || status.includes('trait')) {
        return 'bg-warning text-dark';
      } else if (status.includes('clos') || status.includes('termin')) {
        return 'bg-success';
      } else if (status.includes('encours') || status.includes('en cours')) {
        return 'bg-info';
      } else if (status.includes('nouveau') || status.includes('sans suite')) {
        return 'bg-secondary';
      }
      return 'bg-secondary';
    },

    getAffectationDetails() {
      const details = [];

      // Afficher seulement si c'est une affectation interne (division/secteur/analystes)
      if (this.dossierShowItems.division?.label) {
        details.push(`Division: ${this.dossierShowItems.division.label}`);
      }
      if (this.dossierShowItems.secteur?.label) {
        details.push(`Secteur: ${this.dossierShowItems.secteur.label}`);
      }
      if (this.dossierShowItems.analystes && this.dossierShowItems.analystes.length > 0) {
        const analystesNames = this.dossierShowItems.analystes.map(a => a.firstname || a.name).join(', ');
        details.push(`Analyste(s): ${analystesNames}`);
      }

      return details.join(' | ');
    },

    goToPrevious() {
      if (this.hasPrevious) {
        this.currentIndex--;
        const previousId = this.dossiersList[this.currentIndex].id;
        this.$router.push({ name: 'DossierShow', params: { id: previousId } });
        this.getDossier(previousId);
      }
    },

    goToNext() {
      if (this.hasNext) {
        this.currentIndex++;
        const nextId = this.dossiersList[this.currentIndex].id;
        this.$router.push({ name: 'DossierShow', params: { id: nextId } });
        this.getDossier(nextId);
      }
    },

    async loadDossiersList() {
      try {
        // Charger tous les dossiers sans filtre
        await this.$store.dispatch("dossier/filter/getPagination", {
          filter: "all",
          page: 1,
          limit: 100
        });

        // Attendre un peu que le store se mette à jour
        await this.$nextTick();

        // Récupérer les items depuis le store via mapFields
        if (this.filterItems && this.filterItems.length > 0) {
          this.dossiersList = [...this.filterItems];
          this.updateCurrentIndex();
        }

        // Si la première page ne contient pas tous les dossiers, charger plus
        if (this.filterMeta?.total > this.filterItems?.length) {
          const totalPages = Math.ceil(this.filterMeta.total / 100); // 100 items par page
          console.log('📚 Pagination nécessaire:', totalPages, 'pages au total');

          for (let page = 2; page <= totalPages; page++) {
            await this.$store.dispatch("dossier/filter/getPagination", {
              filter: "all",
              page: page,
              limit: 100
            });

            // Attendre que le store se mette à jour
            await this.$nextTick();

            if (this.filterItems && this.filterItems.length > 0) {
              // Ajouter seulement les nouveaux items
              const newItems = this.filterItems.filter(item =>
                !this.dossiersList.some(existing => existing.id === item.id)
              );
              this.dossiersList = [...this.dossiersList, ...newItems];
              console.log(`📖 Page ${page} chargée:`, newItems.length, 'nouveaux dossiers');
            }
          }
          this.updateCurrentIndex();
        }

      } catch (error) {
        console.error('❌ Erreur lors du chargement de la liste des dossiers:', error);
      }
    },

    updateCurrentIndex() {
      const currentId = parseInt(this.$route.params.id);
      this.currentIndex = this.dossiersList.findIndex(d => d.id === currentId);
    },

    prepareBeforeAffecte() {
      var list_fileds = ["affectation_mode", "affectation_motif", "affectation_observations1"]
      if (this.affectationData.affectation_mode == 1) {
        // Mode interne - vérifier les créations en cours
        if (this.createNewDivision || this.createNewSecteur) {
          notify('Veuillez d\'abord créer la division/secteur avant d\'affecter', 'error')
          return
        }
        list_fileds = ["affectation_mode", "affectation_observations1", "affectation_secteur_id", "affectation_division_id", "affectation_analystes"];
      } else if (this.affectationData.affectation_mode == 2) {
        // Mode administration - ne pas permettre d'affecter si on est en mode création
        if (this.createNewInstitution) {
          notify('Veuillez d\'abord créer l\'institution avant d\'affecter', 'error')
          return
        }
        list_fileds = ["affectation_institution", "affectation_mode"]
      }

      var data = { id: this.dossierShowItems?.id };
      list_fileds.forEach((field) => {
        data[field] = this.affectationData[field]
      })

      this.affectationHandleSubmit(data)
    },

    async getInstitutionTypes() {
      this.institutiontypeIsLoading = true
      try {
        const response = await backend({ method: 'GET', url: '/institution-type' })
        this.institutiontypeItems = response.data.data || []
      } catch (error) {
        console.error('Erreur lors du chargement des types d\'institutions:', error)
        this.institutiontypeItems = []
      } finally {
        this.institutiontypeIsLoading = false
      }
    },

    resetInstitutionSelection() {
      if (this.createNewInstitution) {
        // Vider la sélection d'institution existante
        this.affectationData.affectation_institution_select = null
      }
    },

    async createNewInstitutionOnly() {
      if (!this.newInstitutionData.label) {
        notify('Le nom de l\'institution est obligatoire', 'error')
        return
      }

      this.creatingInstitution = true
      try {
        // Créer l'institution via l'API
        const data = {
          label: this.newInstitutionData.label,
          institutiontype_id: this.newInstitutionData.type || null,
          email: this.newInstitutionData.email || null,
          phone: this.newInstitutionData.phone || null,
        }

        await backend({
          method: 'POST',
          url: '/institutions',
          data
        })

        // Succès
        notify(`Institution "${this.newInstitutionData.label}" créée avec succès !`, 'success')

        // Réinitialiser le formulaire
        this.newInstitutionData = {
          label: '',
          email: '',
          phone: '',
          type: null
        }
        this.createNewInstitution = false

        // Rafraîchir la liste des institutions
        await this.getInstitutions()

        // Afficher un message pour demander de sélectionner
        setTimeout(() => {
          notify('✅ Institution ajoutée ! Sélectionnez-la maintenant dans la liste.', 'info')
        }, 500)

      } catch (error) {
        console.error('Erreur lors de la création de l\'institution:', error)
        const errorMsg = error.response?.data?.message || 'Impossible de créer cette institution'
        notify(errorMsg, 'error')
      } finally {
        this.creatingInstitution = false
      }
    },

    // Méthodes pour Division
    toggleCreateDivision() {
      this.createNewDivision = !this.createNewDivision
      if (this.createNewDivision) {
        this.affectationData.affectation_division = null
      }
    },

    async createNewDivisionOnly() {
      if (!this.newDivisionData.label) {
        notify('Le nom de la division est obligatoire', 'error')
        return
      }

      this.creatingDivision = true
      try {
        const data = {
          label: this.newDivisionData.label,
          code: this.newDivisionData.code || null,
        }

        await backend({
          method: 'POST',
          url: '/divisions',
          data
        })

        notify(`Division "${this.newDivisionData.label}" créée avec succès !`, 'success')

        this.newDivisionData = { label: '', code: '' }
        this.createNewDivision = false

        await this.getDivisions()

        setTimeout(() => {
          notify('✅ Division ajoutée ! Sélectionnez-la maintenant.', 'info')
        }, 500)

      } catch (error) {
        console.error('Erreur lors de la création de la division:', error)
        const errorMsg = error.response?.data?.message || 'Impossible de créer cette division'
        notify(errorMsg, 'error')
      } finally {
        this.creatingDivision = false
      }
    },

    // Méthodes pour Secteur
    toggleCreateSecteur() {
      this.createNewSecteur = !this.createNewSecteur
      if (this.createNewSecteur) {
        this.affectationData.affectation_secteur = null
      }
    },

    async createNewSecteurOnly() {
      if (!this.newSecteurData.label) {
        notify('Le nom du secteur est obligatoire', 'error')
        return
      }
      if (!this.newSecteurData.division_id) {
        notify('Veuillez sélectionner une division', 'error')
        return
      }

      this.creatingSecteur = true
      try {
        const data = {
          label: this.newSecteurData.label,
          code: this.newSecteurData.code || null,
          division_id: this.newSecteurData.division_id,
        }

        await backend({
          method: 'POST',
          url: '/secteurs',
          data
        })

        notify(`Secteur "${this.newSecteurData.label}" créé avec succès !`, 'success')

        this.newSecteurData = { label: '', code: '', division_id: null }
        this.createNewSecteur = false

        await this.getSecteurs()

        setTimeout(() => {
          notify('✅ Secteur ajouté ! Sélectionnez-le maintenant.', 'info')
        }, 500)

      } catch (error) {
        console.error('Erreur lors de la création du secteur:', error)
        const errorMsg = error.response?.data?.message || 'Impossible de créer ce secteur'
        notify(errorMsg, 'error')
      } finally {
        this.creatingSecteur = false
      }
    },
  },

  watch: {
    "affectationData.affectation_mode_select"(val) {
      if (val) {
        this.affectationData.affectation_mode = val.id
      }
    },
    "affectationData.affectation_institution_select"(val) {
      if (val) {
        this.affectationData.affectation_institution = val.id
      }
    },
    "affectationData.affectation_motif_select"(val) {
      if (val) {
        this.affectationData.affectation_motif = val.id
      }
    },
    "affectationData.affectation_analystes_select"(val) {
      this.affectationData.affectation_analystes = val.map(({ id }) => id)
    },
    "affectationData.affectation_secteur"(val) {
      if (val) {
        this.affectationData.affectation_secteur_id = val.id
      }
    },
    "affectationData.affectation_division"(val) {
      if (val) {
        this.affectationData.affectation_division_id = val.id
      }
    },
    affectationPlainteIsCreated(val) {
      if (val) {
        this.$refs.affectationModalClose.click();
        notify(`<div class="ml-2" style="margin-left:10px"><b>Super,La plainte a été traitée</b></div>`, "success")
        this.getDossier(this.$route.params.id ?? 0)
      }
    },
    '$route.params.id'(newId) {
      if (newId) {
        this.updateCurrentIndex();
      }
    },
    filterItems(newItems) {
      if (newItems && newItems.length > 0 && this.dossiersList.length === 0) {
        this.dossiersList = [...newItems];
        this.updateCurrentIndex();
      }
    },
    filterMeta(newMeta) {
      console.log('📊 filterMeta changé:', newMeta);
    },
  },

  mounted() {
    this.getDossier(this.$route.params.id ?? 0);
    this.getAffectations()
    this.getInstitutions()
    this.getInstitutionTypes()
    this.getDivisions()
    this.getSecteurs()
    this.getMotifs()
    this.getUsers()
    this.loadDossiersList()
  },
  unmounted() {
    this.resetAffectations()
    this.resetInstitutions()
    this.resetDivisions()
    this.resetSecteurs()
    this.resetMotifs()
    this.resetAffectationCreate()
    this.resetUsers()
  }
};
</script>

<style scoped>
.card-body h5 {
  font-weight: bold;
}

.badge-ia {
  font-size: 0.9rem;
  margin-bottom: 5px;
  padding: 0.3rem 0.6rem;
  word-break: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  max-width: 100%;
  white-space: normal !important;
}

.card-body p {
  line-height: 1.6;
}

audio {
  background-color: #f8f9fa;
  border-radius: 5px;
}

.input-group .form-control {
  border-radius: 0.25rem 0 0 0.25rem;
}

.flex-wrap.align-items-center {
  gap: 1rem;
}

.d-flex {
  display: flex;
}

.justify-content-between {
  justify-content: space-between;
}

.align-items-center {
  align-items: center;
}

.p-2 {
  padding: 0.5rem;
}

.border-bottom {
  border-bottom: 1px solid #dee2e6;
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.loading-spinner {
  color: white;
  font-size: 24px;
}

.spinner {
  border: 4px solid rgba(255, 255, 255, 0.3);
  border-top: 4px solid white;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
